import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Luggage as LuggageIcon, AlertTriangle, CheckCircle2, Truck,
  Image as ImageIcon, QrCode, Printer, Download,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useEvents } from '../../context/EventContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { post } from '../../api/client';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Stat,
  Table,
  Badge,
  StatusBadge,
  EmptyState,
  SectionTitle,
  Tabs,
  Pagination,
  PersonCell,
  Modal,
  Button,
  Loader,
  Field,
  Input,
  Select,
  SearchInput,
} from '../../components/UI';
import { fmtDateTime, fmtRelative } from '../../utils/format';

const TABS = [
  { key: '', label: 'All bags' },
  { key: 'registered', label: 'Registered' },
  { key: 'collected', label: 'Collected' },
  { key: 'in_transit', label: 'In transit' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'missing', label: 'Missing' },
];

export default function LuggagePage() {
  const { eventId, events, event, hasModule } = useEvents();
  const { isViewer } = useAuth();
  const [qrFor, setQrFor] = useState(null);   // the bag whose tag we are showing
  const [tagOpen, setTagOpen] = useState(false);

  // The desk only exists when the event actually handles bags.
  const handlesLuggage = hasModule('onboardingLuggage') || hasModule('arrivalLuggage');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);

  const { data: summary } = useFetch('/luggage/summary', { event: eventId }, { skip: !eventId });
  const { data: rows, meta, loading, reload } = useFetch(
    '/luggage',
    { event: eventId, page, limit: 25, ...(status ? { status } : {}) },
    { skip: !eventId, withMeta: true },
  );

  if (!events.length) {
    return <EmptyState message="No events available" hint="Luggage tracking begins once you have an event." icon={LuggageIcon} />;
  }

  const visible = search
    ? (rows || []).filter((r) =>
        r.tagNumber?.toLowerCase().includes(search.toLowerCase()) ||
        r.user?.name?.toLowerCase().includes(search.toLowerCase()))
    : rows;

  const byStatus = summary?.byStatus || {};

  return (
    <>
      <SectionTitle
        title="Luggage tracking"
        subtitle="Every bag is tagged with a QR and tracked from collection to the guest's room"
        action={(
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <EventPicker />
            {handlesLuggage && !isViewer && (
              <Button icon={QrCode} onClick={() => setTagOpen(true)} disabled={!eventId}>
                Tag a bag
              </Button>
            )}
          </div>
        )}
      />

      {!handlesLuggage && eventId && (
        <Card padding={15} style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.7 }}>
            This event is not set up for luggage handling. Tick
            <strong style={{ color: 'var(--gold-400)' }}> Luggage handling at onboarding </strong>
            or <strong style={{ color: 'var(--gold-400)' }}>on arrival</strong> on the event to open the desk.
          </p>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
        <Stat label="Total Bags" value={summary?.total ?? 0} icon={LuggageIcon} tone="gold" />
        <Stat label="In Transit" value={byStatus.in_transit || 0} icon={Truck} tone="warning" />
        <Stat label="Delivered" value={summary?.delivered ?? 0} icon={CheckCircle2} tone="success" />
        <Stat label="Missing" value={summary?.missing ?? 0} icon={AlertTriangle} tone={summary?.missing ? 'danger' : 'neutral'} />
      </div>

      {summary?.missing > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card padding={15} style={{
            marginBottom: 16, borderColor: 'rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.05)',
            display: 'flex', alignItems: 'center', gap: 13,
          }}>
            <AlertTriangle size={19} color="var(--danger)" style={{ flexShrink: 0 }} />
            <p style={{ fontSize: 13.3 }}>
              <strong>{summary.missing} bag{summary.missing === 1 ? ' is' : 's are'} reported missing.</strong>
              <span style={{ color: 'var(--text-mid)' }}> Your on-site staff have been alerted.</span>
            </p>
          </Card>
        </motion.div>
      )}

      <Tabs
        active={status}
        onChange={(k) => { setStatus(k); setPage(1); }}
        tabs={TABS.map((t) => ({ ...t, count: t.key ? (byStatus[t.key] || 0) : summary?.total }))}
      />

      <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by tag number or owner…" style={{ maxWidth: 350, marginBottom: 16 }} />

      <Table
        loading={loading}
        rows={visible}
        onRowClick={setDetail}
        empty="No luggage registered for this event yet"
        columns={[
          { key: 'tagNumber', title: 'Tag', render: (r) => (
            <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--gold-400)', fontWeight: 600 }}>
              {r.tagNumber}
            </span>
          )},
          { key: 'user', title: 'Owner', render: (r) => <PersonCell person={r.user} /> },
          { key: 'bagType', title: 'Bag', render: (r) => (
            <div>
              <div style={{ fontSize: 12.5, textTransform: 'capitalize' }}>{r.bagType}</div>
              <div style={{ fontSize: 11, color: 'var(--text-lo)' }}>{r.color || ''}{r.weightKg ? ` · ${r.weightKg} kg` : ''}</div>
            </div>
          )},
          { key: 'room', title: 'Room', render: (r) => r.deliveredRoomNumber || r.room?.roomNumber || '—' },
          { key: 'status', title: 'Status', render: (r) => <StatusBadge status={r.status} /> },
          { key: 'tagQr', title: 'Tag QR', align: 'right', render: (r) => (
            <Button size="sm" variant="subtle" icon={QrCode}
              onClick={(e) => { e.stopPropagation(); setQrFor(r); }}>
              Print
            </Button>
          )},
          { key: 'updatedAt', title: 'Last update', render: (r) => (
            <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>{fmtRelative(r.updatedAt)}</span>
          )},
        ]}
      />
      <Pagination meta={meta} onChange={setPage} />

      <LuggageDetail bag={detail} onClose={() => setDetail(null)} />
      <TagQrModal bag={qrFor} onClose={() => setQrFor(null)} />
      <TagBagModal
        open={tagOpen} onClose={() => setTagOpen(false)}
        eventId={eventId} eventTitle={event?.title}
        onTagged={(bag) => { reload(); setQrFor(bag); }}
      />
    </>
  );
}

/**
 * The luggage desk. A marshal scans the guest's badge — or picks them from the
 * list when the camera is not to hand — and the bag comes back with a tag ready
 * to print and attach.
 */
function TagBagModal({ open, onClose, eventId, eventTitle, onTagged }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [guest, setGuest] = useState(null);
  const [scanned, setScanned] = useState('');
  const [bag, setBag] = useState({ bagType: 'suitcase', color: '', description: '' });
  const [busy, setBusy] = useState(false);

  const { data: people } = useFetch(
    '/users', { event: eventId, role: 'user', limit: 200, ...(search ? { search } : {}) },
    { skip: !open || !eventId },
  );

  const create = async () => {
    if (!guest && !scanned.trim()) return toast.error('Scan a badge or pick the guest');
    setBusy(true);
    try {
      const res = await post('/luggage/tag-guest', {
        event: eventId,
        ...(guest ? { user: guest._id } : { code: scanned.trim() }),
        ...bag,
      });
      toast.success(res.message);
      setGuest(null); setScanned(''); setSearch('');
      setBag({ bagType: 'suitcase', color: '', description: '' });
      onClose();
      // Straight to the printable tag — that is the next thing they need.
      onTagged({ tagNumber: res.data.tagNumber, user: res.data.guest });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose} width={520}
      title="Tag a bag"
      subtitle={eventTitle}
      footer={(
        <>
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={create} loading={busy}>Create tag</Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <Field label="Scan the guest's badge" hint="Or pick them from the list below">
          <Input
            value={scanned}
            onChange={(e) => { setScanned(e.target.value); setGuest(null); }}
            placeholder="Scanner output appears here…"
            autoFocus
          />
        </Field>

        {!scanned.trim() && (
          <Field label="Guest" required>
            <SearchInput
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, employee code or mobile…"
              style={{ marginBottom: 9 }}
            />
            <div style={{
              maxHeight: 190, overflowY: 'auto',
              border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: 5,
            }}>
              {!people?.length && (
                <p style={{ fontSize: 12.5, color: 'var(--text-lo)', padding: 9 }}>Nobody matches that search.</p>
              )}
              {(people || []).slice(0, 40).map((p) => (
                <button
                  key={p._id} type="button" onClick={() => setGuest(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '8px 9px', borderRadius: 'var(--r-sm)', textAlign: 'left', fontSize: 13,
                    background: guest?._id === p._id ? 'rgba(239,212,138,0.1)' : 'transparent',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block' }}>{p.name}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>
                      {[p.employeeCode, p.mobile].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {guest?._id === p._id && <CheckCircle2 size={15} color="var(--ok)" />}
                </button>
              ))}
            </div>
          </Field>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
          <Field label="Bag type">
            <Select value={bag.bagType} onChange={(e) => setBag({ ...bag, bagType: e.target.value })}>
              {['suitcase', 'backpack', 'duffel', 'briefcase', 'other'].map((t) => (
                <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>
              ))}
            </Select>
          </Field>
          <Field label="Colour">
            <Input value={bag.color} onChange={(e) => setBag({ ...bag, color: e.target.value })} placeholder="Navy" />
          </Field>
        </div>

        <Field label="Anything distinguishing?" hint="Optional">
          <Input value={bag.description} onChange={(e) => setBag({ ...bag, description: e.target.value })}
            placeholder="Red ribbon on the handle" />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * The printable bag tag. The QR opens the public page for this bag, so whoever
 * is holding it can see whose it is and which room it goes to.
 */
function TagQrModal({ bag, onClose }) {
  const { data, loading } = useFetch(
    bag ? `/luggage/public/${bag.tagNumber}` : null, {}, { skip: !bag },
  );

  if (!bag) return null;

  // The image comes from our own API, so no tag URL is handed to a third party.
  const qr = data?.qrImage;

  const print = () => {
    if (!qr) return;
    const w = window.open('', '_blank', 'width=420,height=620');
    if (!w) return;
    w.document.write(`
      <html><head><title>${bag.tagNumber}</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;text-align:center;padding:26px;margin:0}
        .tag{border:2px solid #111;border-radius:14px;padding:22px;display:inline-block;min-width:250px}
        .code{font-size:23px;font-weight:800;letter-spacing:2px;margin:14px 0 4px}
        .name{font-size:17px;font-weight:600}
        .meta{font-size:12px;color:#555;margin-top:6px}
        img{width:210px;height:210px}
      </style></head>
      <body onload="window.print()">
        <div class="tag">
          <div class="name">${(data?.guest?.name || bag.user?.name || '').replace(/</g, '')}</div>
          <div class="meta">${(data?.event?.title || '').replace(/</g, '')}</div>
          <img src="${qr}" alt="" />
          <div class="code">${bag.tagNumber}</div>
          <div class="meta">${data?.roomNumber ? `Room ${data.roomNumber}` : 'Room to be allocated'}</div>
        </div>
      </body></html>`);
    w.document.close();
  };

  return (
    <Modal
      open={Boolean(bag)} onClose={onClose} width={400}
      title="Bag tag" subtitle={bag.tagNumber}
      footer={(
        <>
          <Button variant="subtle" onClick={onClose}>Close</Button>
          {qr && (
            <a href={qr} download={`${bag.tagNumber}.png`} style={{ textDecoration: 'none' }}>
              <Button variant="outline" icon={Download}>Download</Button>
            </a>
          )}
          <Button icon={Printer} onClick={print} disabled={!qr}>Print tag</Button>
        </>
      )}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'inline-block', padding: 16, background: '#fff', borderRadius: 'var(--r-lg)', minHeight: 200 }}>
          {qr
            ? <img src={qr} alt="Bag tag QR" style={{ width: 200, height: 200, display: 'block' }} />
            : <div style={{ width: 200, height: 200, display: 'grid', placeItems: 'center' }}><Loader /></div>}
        </div>

        <p style={{
          fontFamily: 'var(--font-display)', fontSize: 21, fontWeight: 700,
          letterSpacing: 1.5, color: 'var(--gold-300)', marginTop: 15,
        }}>
          {bag.tagNumber}
        </p>

        {loading ? <Loader /> : data && (
          <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 6, lineHeight: 1.7 }}>
            {data.guest.name}
            <br />
            {data.hotel?.name || 'Hotel not assigned'}
            {' · '}
            {data.roomNumber ? `Room ${data.roomNumber}` : 'Room not allocated yet'}
          </p>
        )}

        <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 12, lineHeight: 1.6 }}>
          Scanning this opens a page showing the owner and their room — which
          fills in automatically once the hotel allocates one.
        </p>
      </div>
    </Modal>
  );
}

function LuggageDetail({ bag, onClose }) {
  if (!bag) return null;

  return (
    <Modal
      open={Boolean(bag)} onClose={onClose} width={540}
      title={`Bag ${bag.tagNumber}`}
      subtitle={`${bag.user?.name || 'Unknown owner'} · ${bag.bagType}${bag.color ? `, ${bag.color}` : ''}`}
    >
      <div style={{ display: 'flex', gap: 11, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatusBadge status={bag.status} />
        {bag.confirmedByUser && <Badge tone="success">Receipt confirmed</Badge>}
        {(bag.deliveredRoomNumber || bag.room?.roomNumber) && (
          <Badge tone="gold">Room {bag.deliveredRoomNumber || bag.room?.roomNumber}</Badge>
        )}
      </div>

      {bag.images?.length > 0 && (
        <>
          <p style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.9, color: 'var(--text-lo)', marginBottom: 10 }}>
            Photos uploaded by the guest
          </p>
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 22 }}>
            {bag.images.map((src, i) => (
              <div key={i} style={{
                width: 92, height: 92, borderRadius: 'var(--r)', overflow: 'hidden',
                border: '1px solid var(--border)', background: `url(${src}) center/cover`,
                display: 'grid', placeItems: 'center',
              }}>
                {!src && <ImageIcon size={18} color="var(--text-lo)" />}
              </div>
            ))}
          </div>
        </>
      )}

      <p style={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.9, color: 'var(--text-lo)', marginBottom: 12 }}>
        Handling timeline
      </p>
      {bag.timeline?.length ? (
        <div style={{ position: 'relative', paddingLeft: 22 }}>
          <div style={{ position: 'absolute', left: 6, top: 6, bottom: 6, width: 1.5, background: 'var(--border)' }} />
          {[...bag.timeline].reverse().map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
              style={{ position: 'relative', paddingBottom: 18 }}
            >
              <span style={{
                position: 'absolute', left: -22, top: 3, width: 13, height: 13, borderRadius: '50%',
                background: i === 0 ? 'var(--gold-400)' : 'var(--ink-700)',
                border: '2px solid var(--ink-850)',
              }} />
              <div style={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>
                {String(t.status || '').replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 2 }}>
                {fmtDateTime(t.at)}
                {t.by?.name ? ` · ${t.by.name}` : ''}
                {t.location ? ` · ${t.location}` : ''}
              </div>
              {t.note && <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 4 }}>{t.note}</div>}
            </motion.div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>No handling events recorded yet.</p>
      )}

      {bag.missingNote && (
        <div style={{
          marginTop: 16, padding: 13, background: 'var(--danger-dim)',
          border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--r)',
        }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', marginBottom: 4 }}>Missing report</p>
          <p style={{ fontSize: 12.5, color: 'var(--text-mid)' }}>{bag.missingNote}</p>
        </div>
      )}
    </Modal>
  );
}
